import React from 'react'

import ClipboardInput from 'components/common/clipboard-input'
import HelpTooltip from 'components/common/tooltip'
import { Box } from 'components/ds'
import ButtonContainer from 'components/ui//button-container'
import Button from 'components/ui/button'
import Label from 'components/ui/label'
import QRCodeButton from 'components/ui/qr-code-button'

type Props = {
  disabled?: boolean
  label?: React.ReactNode
  tooltipId?: string
  hideNewWindow?: boolean
  url: string
  onCopy: () => void
  onDisable?: () => void
  onPreview?: () => void
  enableQR?: boolean
  qrFilename?: string
  onQrAction?: (action: 'download' | 'copy') => void
}

/**
 * A URL sharing component with copy functionality, preview, and optional disable actions
 * @param disabled - Whether the component is disabled - defaults to false
 * @param label - Optional label text to display above the URL input
 * @param tooltipId - Optional tooltip ID to show help tooltip next to the label
 * @param url - The URL to display and copy
 * @param onCopy - Callback function called when the copy action is triggered
 * @param onDisable - Optional callback function called when the disable button is clicked
 * @param onPreview - Optional callback function called when the preview button is clicked
 * @param enableQR - Optional boolean to turn on functionality to copy/download QR instead of link
 * @returns React fragment containing label, clipboard input, and action buttons
 */
const CopyUrlItem = ({
  disabled,
  label,
  tooltipId,
  hideNewWindow,
  url,
  onCopy,
  onDisable,
  onPreview,
  enableQR,
  qrFilename,
  onQrAction,
}: Props) => {
  return (
    <>
      {Boolean(label) && (
        <Label
          caption={
            <>
              {label}
              {tooltipId && <HelpTooltip id={tooltipId} />}
            </>
          }
        />
      )}
      <ButtonContainer compact={enableQR}>
        <Box flexGrow="1">
          <ClipboardInput
            disabled={disabled}
            readOnly
            value={url}
            onCopy={onCopy}
          />
        </Box>
        {!hideNewWindow && (
          <Button
            appearance={enableQR ? 'transparent' : 'icon'}
            disabled={disabled || !url}
            icon="open-in-new-tab"
            link={url}
            linkTarget="_blank"
            onClick={onPreview}
            tooltip="Open in new tab"
          />
        )}

        {enableQR && (
          <QRCodeButton
            url={url}
            filename={qrFilename}
            disabled={disabled}
            onAction={onQrAction}
          />
        )}
        {onDisable && (
          <Button
            appearance="danger"
            disabled={disabled || !url}
            icon="cancel"
            onClick={onDisable}
            tooltip="Disable share"
          />
        )}
      </ButtonContainer>
    </>
  )
}

export default React.memo<Props>(CopyUrlItem)
